import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpService } from '../../../utils/apiService';

export const factoryQueryId = (factoryId) => {
  if (factoryId == null || factoryId === '' || factoryId === 'fallback') return 'fallback';
  return String(factoryId);
};

const invalidateTou = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['touPricingList'] });
  queryClient.invalidateQueries({ queryKey: ['touPricingDetail'] });
  queryClient.invalidateQueries({ queryKey: ['touPricingApplicable'] });
  queryClient.invalidateQueries({ queryKey: ['touMonthStatus'] });
  queryClient.invalidateQueries({ queryKey: ['touReadiness'] });
};

export const useFetchTouMonthStatus = (operatorId, year, month, enabled = true) => {
  return useQuery({
    queryKey: ['touMonthStatus', operatorId, year, month],
    queryFn: async () => {
      const response = await httpService.get(
        `/tou-pricing/month-status?operatorId=${operatorId}&year=${year}&month=${month}`,
      );
      return response.data.data;
    },
    enabled: !!operatorId && !!year && !!month && enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchTouReadiness = (operatorId, year, month, enabled = true) => {
  return useQuery({
    queryKey: ['touReadiness', operatorId, year, month],
    queryFn: async () => {
      const response = await httpService.get(
        `/report/tou-readiness?operatorId=${operatorId}&year=${year}&month=${month}`,
      );
      return response.data.data;
    },
    enabled: !!operatorId && !!year && !!month && enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchTouPricingList = (operatorId, year) => {
  return useQuery({
    queryKey: ['touPricingList', operatorId, year],
    queryFn: async () => {
      const response = await httpService.get(
        `/tou-pricing/list?operatorId=${operatorId}&year=${year}`,
      );
      return response.data.data;
    },
    enabled: !!operatorId && !!year,
    refetchOnWindowFocus: false,
  });
};

export const useFetchTouPricingDetail = (operatorId, year, month, enabled, factoryId = 'fallback') => {
  const factory = factoryQueryId(factoryId);
  return useQuery({
    queryKey: ['touPricingDetail', operatorId, year, month, factory],
    queryFn: async () => {
      const response = await httpService.get(
        `/tou-pricing/detail?operatorId=${operatorId}&year=${year}&month=${month}&factoryId=${factory}`,
      );
      return response.data.data;
    },
    enabled: !!operatorId && !!year && !!month && !!enabled,
    refetchOnWindowFocus: false,
  });
};

export const useUpsertTouPricing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const response = await httpService.post('/tou-pricing/upsert', body);
      return response.data;
    },
    onSuccess: () => invalidateTou(queryClient),
  });
};

export const useCopyTouPricing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const response = await httpService.post('/tou-pricing/copy', body);
      return response.data;
    },
    onSuccess: () => invalidateTou(queryClient),
  });
};

export const useFetchTouApplicable = (operatorId, from, to) => {
  return useQuery({
    queryKey: ['touPricingApplicable', operatorId, from, to],
    queryFn: async () => {
      const response = await httpService.get(
        `/tou-pricing/applicable?operatorId=${operatorId}&from=${from}&to=${to}`,
      );
      return response.data.data;
    },
    enabled: !!operatorId && !!from && !!to,
    refetchOnWindowFocus: false,
  });
};
